import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { FilterBar } from "../../components/ui/FilterBar";
import { PageHeader } from "../../components/ui/PageHeader";
import { ReportSummaryCards } from "../../components/ui/ReportSummaryCards";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import { type AppAdoptionCategory, type AppAdoptionRow, employeesApi } from "../../lib/api/employees";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function categoryTone(
  category: AppAdoptionCategory,
): "neutral" | "success" | "warning" | "danger" {
  if (category === "no_device") return "danger";
  if (category === "device_never_seen" || category === "device_stale") return "warning";
  if (category === "active") return "success";
  return "neutral";
}

const DAYS_OPTIONS = [7, 14, 30, 90] as const;
const STALE_HOURS_OPTIONS = [24, 48, 72] as const;

export function AppAdoptionReportPage() {
  useLocale();
  const [days, setDays] = useState<number>(7);
  const [staleHours, setStaleHours] = useState<number>(24);

  const query = useQuery({
    queryKey: ["app-adoption", days, staleHours],
    queryFn: () => employeesApi.appAdoption({ days, stale_hours: staleHours }),
  });

  const columns: Column<AppAdoptionRow>[] = [
    {
      key: "employee_id",
      header: t("reports.appAdoption.column.employeeId"),
      render: (row) => <span className="mono">{row.employee_id}</span>,
    },
    {
      key: "full_name",
      header: t("reports.appAdoption.column.fullName"),
      render: (row) => row.full_name || "—",
    },
    {
      key: "phone",
      header: t("reports.appAdoption.column.phone"),
      render: (row) => row.phone || "—",
    },
    {
      key: "category",
      header: t("reports.appAdoption.column.category"),
      render: (row) => (
        <Badge tone={categoryTone(row.category)}>
          {t(`reports.appAdoption.category.${row.category}`, row.category)}
        </Badge>
      ),
    },
    {
      key: "badge_days_in_window",
      header: t("reports.appAdoption.column.badgeDays"),
      render: (row) => <span className="num">{row.badge_days_in_window}</span>,
      align: "right",
    },
    {
      key: "device_count",
      header: t("reports.appAdoption.column.devices"),
      render: (row) => <span className="num">{row.device_count}</span>,
      align: "right",
    },
    {
      key: "newest_device_last_seen_at",
      header: t("reports.appAdoption.column.newestDeviceSeen"),
      render: (row) => formatDateTime(row.newest_device_last_seen_at),
    },
    {
      key: "last_entry_at",
      header: t("reports.appAdoption.column.lastEntry"),
      render: (row) => formatDateTime(row.last_entry_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("reports.appAdoption.title")}
        subtitle={t("reports.appAdoption.subtitle")}
      />

      <div className="stack">
        {query.data && (
          <ReportSummaryCards
            metrics={[
              {
                labelKey: "reports.appAdoption.summary.totalBadgeActive",
                value: query.data.summary.total_badge_active,
              },
              {
                labelKey: "reports.appAdoption.summary.noDevice",
                value: query.data.summary.no_device,
              },
              {
                labelKey: "reports.appAdoption.summary.deviceNeverSeen",
                value: query.data.summary.device_never_seen,
              },
              {
                labelKey: "reports.appAdoption.summary.deviceStale",
                value: query.data.summary.device_stale,
              },
              {
                labelKey: "reports.appAdoption.summary.active",
                value: query.data.summary.active,
              },
              {
                labelKey: "reports.appAdoption.summary.adoptionRate",
                value: `${Math.round(query.data.summary.adoption_rate * 100)}%`,
              },
            ]}
          />
        )}

        <FilterBar>
          <span className="filter-bar__label">{t("reports.appAdoption.filter.days")}</span>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {t(`reports.appAdoption.filter.days${d}`, String(d))}
              </option>
            ))}
          </Select>
          <span className="filter-bar__label">
            {t("reports.appAdoption.filter.staleHours")}
          </span>
          <Select value={staleHours} onChange={(e) => setStaleHours(Number(e.target.value))}>
            {STALE_HOURS_OPTIONS.map((h) => (
              <option key={h} value={h}>
                {t(`reports.appAdoption.filter.staleHours${h}`, String(h))}
              </option>
            ))}
          </Select>
        </FilterBar>

        <Card padding="none">
          {query.isLoading && <LoadingState />}
          {query.error && (
            <ErrorState
              titleKey="states.errorTitle"
              message={translateError(query.error)}
              action={
                <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
                  {t("common.retry")}
                </Button>
              }
            />
          )}
          {query.data && (
            <Table
              rows={query.data.rows}
              columns={columns}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  titleKey="reports.appAdoption.emptyTitle"
                  bodyKey="reports.appAdoption.emptyBody"
                />
              }
            />
          )}
        </Card>
      </div>
    </div>
  );
}
